import type { MaterialInfo, MaterialValue } from '@rogue/rackbuilder'
import type { RackOptions } from '../../../../RackTypes'
import { COLOR_TO_MATERIAL_KEYS } from '../../../../constants'

/**
 * Base material strategy with shared logic for both RM and RML
 */
export abstract class BaseMaterialStrategy {
  protected isTxtGray

  constructor(protected config: RackOptions) {
    this.isTxtGray = this.config.materials?.uprights?.main?.color === '#5b5b59'
      && this.config.materials?.uprights?.main?.material === 'powdercoat'
  }
  
  createMainMaterialValue(): MaterialValue {
    const material: MaterialValue = { name: 'metal', material: 'base' }
    
    // RML-3 is welded and uses powdercoat
    if (this.config.isRML3) {
      material.material = 'powdercoat'
      
      return material
    }
    
    // Hex color (custom MG colors)
    if (this.config.color?.startsWith('#')) {
      material.material = 'mg'
      material.color = this.config.color
      
      return material
    }
    
    // Special finishes (stainless, powdercoat, satinClear)
    if (this.config.color === 'stainless' || this.config.color === 'powdercoat' || this.config.color === 'satinClear') {
      material.material = this.config.color
      
      return material
    }
    
    // Default: MG with color
    material.material = 'mg'
    material.color = this.config.color
    
    return material
  }
  
  createMainMaterial(): MaterialInfo {
    const mainMaterial = this.createMainMaterialValue()
    
    return {
      main     : mainMaterial,
      hardware : { material: this.config.hardware },
      ...this.config.materials?.uprights, // Override if explicitly set
    }
  }

  // The 70" crossmember (CAVE_RM "crossmember top 7") is only manufactured in MG black, so it takes neither the rack's color nor the txtGray treatment - same fixed material caveCrossmemberParts gives every Cave crossmember.
  createWideCrossmemberMaterials(): MaterialInfo {
    return {
      main     : { material: 'mg', color: 'black' },
      hardware : { material: this.config.hardware },
    }
  }

  createCrossmemberMaterials(): MaterialInfo {
    const mainMaterial = this.createMainMaterialValue()

    // If txtGray, crossmembers should be powdercoat
    if (this.isTxtGray) {
      return {
        main     : { material: 'powdercoat' },
        hardware : { material: this.config.hardware },
      }
    }
    
    // Override with config if specified
    if (this.config.materials?.crossmembers) {
      return {
        main     : mainMaterial,
        hardware : { material: this.config.hardware },
        ...this.config.materials.crossmembers,
      }
    }
    
    // Default: same as main material
    return {
      main     : mainMaterial,
      hardware : { material: this.config.hardware },
    }
  }
  
  createAddonMaterials(): MaterialInfo {
    const ghostMetalMaterials: MaterialValue[] = [
      { name: 'unpainted', color: 'white', config: { opacity: 0.01 } },
      { name: 'metal', color: this.config.color, config: { opacity: 0.2 } },
    ]
    
    return {
      main     : ghostMetalMaterials,
      hardware : { config: { opacity: 0.2 } },
    }
  }

  createFrontCrossmemberAddonMaterials(): MaterialInfo {
    return this.createAddonMaterials()
  }
  
  createSocketPullupMaterials(color: string, knurl?: string): MaterialInfo {
    if (this.config.materials?.pullup) {
      return this.config.materials.pullup as MaterialInfo
    }
    
    const barMaterials: MaterialValue[] = [
      { name: 'bar', material: color as MaterialValue['material'] },
    ]
    
    if (knurl === 'knurl') {
      barMaterials.push({ 
        name     : 'knurl', 
        material : 'knurl', 
        color    : color === 'matte' ? '#666666' : '#cccccc', 
      })
    } else {
      barMaterials.push({ name: 'knurl', material: color as MaterialValue['material'] })
    }
    
    let roguePullupSticker = null

    if (color === 'stainless') {
      roguePullupSticker = { color: '#cccccc', material: 'stainless', config: { opacity: 0 } } as MaterialInfo
    }
    
    return {
      bar  : barMaterials,
      logo : roguePullupSticker as MaterialInfo,
    }
  }
  
  createCrownMaterials(): MaterialInfo {
    const crownMaterials = JSON.parse(JSON.stringify(this.createMainMaterial())) as MaterialInfo
    
    if (this.config.materials?.crown) {
      crownMaterials.beam = this.config.materials.crown.beam
    }
    
    return crownMaterials
  }
  
  createMiddleOptionMaterials(): MaterialInfo {
    return {
      hardware: { material: this.config.hardware },
    }
  }
  
  // frontCrossmemberOverride defaults to this.config.materials?.frontCrossmember, which only Hybrid sets - every other product leaves both unset and keeps taking the rack's own color.
  createFrontCrossmemberMaterials(frontCrossmemberOverride?: MaterialInfo): MaterialInfo {
    const frontCrossmember = frontCrossmemberOverride ?? this.config.materials?.frontCrossmember

    if (frontCrossmember) {
      return {
        main     : frontCrossmember.main,
        hardware : { material: this.config.hardware },
      }
    }

    let frontMaterials: MaterialInfo = {
      main     : { name: 'metal', color: this.config.color },
      hardware : { material: this.config.hardware },
    }

    if (COLOR_TO_MATERIAL_KEYS.includes(this.config.color as string)) {
      frontMaterials = { 
        main     : { material: this.config.color as MaterialValue['material'] }, 
        hardware : { material: this.config.hardware }, 
      }
    }
    
    return frontMaterials
  }
  
  // Abstract methods - must be implemented by RM/RML strategies
  abstract createNameplateMaterials(nameplateOverride?: MaterialInfo, backOverride?: RackOptions['back']): MaterialInfo
  abstract createBackCrossmemberMaterials(backOverride?: RackOptions['back'], topCrossmemberOverride?: MaterialInfo): MaterialInfo
  abstract createBackStabilizerMaterials(): MaterialInfo
}